/**
 * ReconteoApiClient -- HTTP client para el reconteo INLINE de un caso con
 * discrepancia (ActionArea reconteo-decisions, vista renderReconteoInline).
 *
 * Mision: RECONTEO-INLINE-F1-4-001.
 *   T4 (panel renderReconteoInline)  -- DONE-STUB bitacora #1488
 *   T5 (panel state machine reconteos max 2) -- DONE-STUB bitacora #1497
 *   T2 (backend Argy-A endpoint /reconteo/recount) -- DONE bitacora #1499
 *   T3 (workflow n8n incluye umbral_cantidad) -- DONE bitacora #1506
 *   T7 (sync trigger decisiones jsonb) -- DONE bitacora #1503
 *
 * Endpoint productivo (Argy-A NexoAPI v1.6.25+):
 *   POST /api/v1/reconteo/recount
 *     body: { logId: uuid, conteoRecontado: number }
 *     resp: {
 *       cuadra: bool,
 *       intentos: number,           -- numero total de intentos consumidos para este log_id
 *       intentosRestantes: number,  -- MAX (2) - intentos
 *       decision: string            -- 'recontado_ok' | 'recontar' | 'aceptar' | 'pending_admin'
 *     }
 *     errors:
 *       400 -- conteoRecontado <= 0
 *       404 -- log_id no existe
 *       409 -- decision ya terminal (aceptar / pending_admin)
 *       422 -- reconteos agotados (>= 2)
 *
 * Este cliente normaliza el response del backend al shape esperado por el
 * consumer (renderReconteoInline state machine T5):
 *   { cuadra, reconteosIntentos, intentosRestantes, decisionAuto, decision }
 *
 * @module infrastructure/api/ReconteoApiClient
 */

import { ApiBase } from './ApiBase.js';

const ENDPOINT = '/api/v1/reconteo/recount';

/**
 * Adapter del response del backend al shape que consume el state machine.
 * El backend devuelve `intentos`/`decision`; el panel usa `reconteosIntentos`/`decisionAuto`
 * para mantener consistencia con el state local (`casos[i].reconteosIntentos`).
 */
function adaptResponse(api) {
  if (!api) return null;
  return {
    cuadra: !!api.cuadra,
    reconteosIntentos: Number(api.intentos ?? 0),
    intentosRestantes: Number(api.intentosRestantes ?? 0),
    decision: api.decision || null,                                 // raw del backend
    decisionAuto: api.cuadra ? 'auto-accept' : null,               // shortcut: true si transicion automatica
    diffSignedNuevo: typeof api.diffSignedNuevo === 'number' ? api.diffSignedNuevo : null
  };
}

export const ReconteoApiClient = {
  /**
   * Registra el reconteo de un caso con discrepancia.
   * El backend persiste el reconteo en `verificacion_discrepancia_log.conversacion.reconteos[]`
   * y devuelve si cuadra con el stock del sistema (a ciegas del operador).
   *
   * @param {string|number} logId -- uuid de la fila verificacion_discrepancia_log
   * @param {number} conteoRecontado -- total recontado (suma de sub-conteos si multi-estado)
   * @param {object} [subConteos] -- map { label: cantidad } si multi-estado. NO se envia al
   *     backend (que solo recibe el total); se acepta por compat con el consumer que lo
   *     pasa, util para futura extension del endpoint o telemetria local.
   * @returns {Promise<{ok:true, data:{cuadra:boolean, reconteosIntentos:number, intentosRestantes:number, decision:string|null, decisionAuto:string|null, diffSignedNuevo:number|null}} | {ok:false, error:{code:number|string, message:string, data?:object}}>}
   */
  async recount(logId, conteoRecontado, subConteos = null) {
    // Validacion local devuelve el mismo shape canonico de CrudResult.fail
    // (error: msg_string, code: code_string) que ApiBase para que el consumer
    // pueda leer res.error y res.code uniformemente.
    if (logId == null) return { ok: false, error: 'logId requerido', code: 'LOG_ID_REQUIRED' };
    const n = Number(conteoRecontado);
    // CRITICAL bit #5232/#5233: el 0 ES un reconteo valido (insumo agotado —
    // caso real Agua Con Gas stock 15 / real 0). El guard viejo `n <= 0`
    // bloqueaba aqui lo que el modal ya permitia confirmar. Solo se rechaza
    // negativo o no-numerico. OJO: el backend (ReconteoService + DTO Range)
    // tambien rechaza 0 — fix de Argy-A requerido para el ciclo completo.
    if (conteoRecontado == null || isNaN(n) || n < 0) {
      return { ok: false, error: 'conteoRecontado debe ser un numero >= 0', code: 'CANTIDAD_INVALIDA' };
    }

    // REC-T5 (bit #3180): subConteos se ACEPTABA como param pero nunca viajaba en
    // el body -- el backend solo recibia el total, sin el desglose por sub-estado.
    const body = { logId, conteoRecontado: n };
    if (subConteos && typeof subConteos === 'object') body.subConteos = subConteos;
    const res = await ApiBase.post(ENDPOINT, body);
    if (!res.ok) return res;

    // El backend devuelve ApiResponse<T> o el T directo segun la convencion. El fallback
    // res.data?.data || res.data normaliza ambas formas (matching pattern InsumosApiClient).
    const apiData = res.data?.data || res.data || {};
    return { ok: true, data: adaptResponse(apiData) };
  }
};
